import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import axiosInstance from '../../api/axiosInstance';

const AdminPmDetail = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConversation();
  }, [conversationId]);

  const loadConversation = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/admin/pm/conversation/${conversationId}`);
      setConversation(response.data.conversation);
    } catch (error) {
      console.error('Fehler beim Laden der Konversation:', error);
      setError('Fehler beim Laden der Konversation');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, reason = '') => {
    try {
      await axiosInstance.post(`/admin/pm/conversation/${conversationId}/action`, {
        action,
        reason
      });
      loadConversation(); // Neu laden
    } catch (error) {
      console.error('Fehler bei Konversations-Aktion:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="alert alert-error">
          <span>{error || 'Konversation nicht gefunden'}</span>
        </div>
        <button 
          className="btn btn-primary mt-4"
          onClick={() => navigate('/admin/pm')}
        >
          ← Zurück zur Übersicht
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button 
            className="btn btn-ghost"
            onClick={() => navigate('/admin/pm')}
          >
            ← Zurück
          </button>
          <h1 className="text-2xl font-bold">PM-Konversation Details</h1>
        </div>
        
        <div className="flex gap-2">
          <div className="dropdown dropdown-end">
            <label tabIndex={0} className="btn btn-outline">
              Aktionen
            </label>
            <ul tabIndex={0} className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52">
              <li>
                <button 
                  onClick={() => handleAction(conversation.isHidden ? 'unhide' : 'hide')}
                  className={conversation.isHidden ? 'text-success' : 'text-warning'}
                >
                  {conversation.isHidden ? '👁️ Einblenden' : '🙈 Verstecken'}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleAction(conversation.isFlagged ? 'unflag' : 'flag')}
                  className={conversation.isFlagged ? 'text-success' : 'text-error'}
                >
                  {conversation.isFlagged ? '🚩 Entflaggen' : '🚩 Flaggen'}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Konversation Info */}
        <div className="lg:col-span-1">
          <div className="bg-base-100 p-6 rounded-lg shadow">
            <h3 className="text-lg font-bold mb-4">📋 Konversations-Info</h3>
            
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500">Status</div>
                <div className="flex gap-2 mt-1">
                  {conversation.isHidden && (
                    <div className="badge badge-warning">Versteckt</div>
                  )}
                  {conversation.isFlagged && (
                    <div className="badge badge-error">Gemeldet</div>
                  )}
                  {!conversation.isHidden && !conversation.isFlagged && (
                    <div className="badge badge-success">Normal</div>
                  )}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500">Erstellt</div>
                <div className="text-sm">
                  {conversation.createdAt && !isNaN(new Date(conversation.createdAt).getTime())
                    ? formatDistanceToNow(new Date(conversation.createdAt), { 
                        addSuffix: true, 
                        locale: de 
                      })
                    : 'Unbekannt'
                  }
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500">Letzte Aktivität</div>
                <div className="text-sm">
                  {conversation.lastMessageAt && !isNaN(new Date(conversation.lastMessageAt).getTime()) 
                    ? formatDistanceToNow(new Date(conversation.lastMessageAt), { 
                        addSuffix: true, 
                        locale: de 
                      })
                    : 'Unbekannt'
                  }
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500">Nachrichten</div>
                <div className="flex gap-2 items-center">
                  <div className="badge badge-info">{conversation.totalMessages}</div>
                  {conversation.deletedMessages > 0 && (
                    <div className="badge badge-warning">
                      {conversation.deletedMessages} gelöscht
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Teilnehmer */}
          <div className="bg-base-100 p-6 rounded-lg shadow mt-6">
            <h3 className="text-lg font-bold mb-4">👥 Teilnehmer</h3>
            <div className="space-y-3">
              {conversation.participants.map((participant) => (
                <div key={participant._id} className="flex items-center gap-3">
                  <div className="avatar">
                    <div className="w-8 h-8 rounded-full">
                      <img 
                        src={participant.avatar || '/default-avatar.png'} 
                        alt={participant.username}
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{participant.username}</div>
                    <div className="text-xs text-gray-500">{participant.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {participant.isOnline && (
                      <div className="badge badge-success badge-xs">Online</div>
                    )}
                    <button 
                      className="btn btn-xs btn-outline"
                      onClick={() => navigate(`/admin/users/${participant._id}`)}
                    >
                      Profil
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Nachrichten */}
        <div className="lg:col-span-2">
          <div className="bg-base-100 p-6 rounded-lg shadow">
            <h3 className="text-lg font-bold mb-4">💬 Nachrichten</h3>
            
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {conversation.messages && conversation.messages.length > 0 ? (
                conversation.messages.map((message) => (
                <div 
                  key={message._id} 
                  className={`p-4 rounded-lg ${
                    message.isDeleted 
                      ? 'bg-red-50 border border-red-200' 
                      : 'bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <div className="avatar">
                        <div className="w-6 h-6 rounded-full">
                          <img 
                            src={message.senderId.avatar || '/default-avatar.png'} 
                            alt={message.senderId.username}
                          />
                        </div>
                      </div>
                      <span className="font-medium text-sm">
                        {message.senderId.username}
                      </span>
                      {message.isDeleted && (
                        <div className="badge badge-error badge-xs">Gelöscht</div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {message.createdAt && !isNaN(new Date(message.createdAt).getTime())
                        ? formatDistanceToNow(new Date(message.createdAt), { 
                            addSuffix: true, 
                            locale: de 
                          })
                        : 'Unbekannt'
                      }
                    </div>
                  </div>
                  
                  <div className={`text-sm ${message.isDeleted ? 'text-red-600' : ''}`}>
                    {message.isDeleted ? (
                      <div>
                        <div className="italic text-red-500 mb-2">
                          Diese Nachricht wurde gelöscht.
                        </div>
                        {message.deletedInfo && (
                          <div className="text-xs text-red-400">
                            Gelöscht: {message.deletedInfo.deletedAt && !isNaN(new Date(message.deletedInfo.deletedAt).getTime())
                              ? formatDistanceToNow(new Date(message.deletedInfo.deletedAt), { 
                                  addSuffix: true, 
                                  locale: de 
                                })
                              : 'Unbekannt'
                            }
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap break-words">
                        {message.content}
                      </div>
                    )}
                  </div>

                  {/* Anhänge */}
                  {message.attachments && message.attachments.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-500 mb-1">Anhänge:</div>
                      <div className="flex flex-wrap gap-1">
                        {message.attachments.map((attachment, idx) => (
                          <div key={idx} className="badge badge-outline badge-sm">
                            📎 {attachment.filename}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-8">
                  {conversation.messages === undefined ? 'Nachrichten werden geladen...' : 'Keine Nachrichten gefunden'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPmDetail;
